import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme, Typography } from '@material-ui/core';
import MUIRichTextEditor from 'mui-rte';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getMaxTickLimit, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery, Query } from 'react-apollo';
import { RvLoader } from '../../components/Loader';
import glamorous from 'glamorous';
import { SelectedItemProps } from '../MattersByStatusChart';
import HelpIconPopover from '../../components/HelpIconPopover';
import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, Scale, Tick, Chart } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { DrillDownType } from '../DrillDownType';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { ChartEmptyState } from '../ChartEmptyState';
import ContactMailOutlinedIcon from '@material-ui/icons/ContactMailOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            // padding: 15,
            display: 'flex',
            flexFlow: 'column',
            textAlign: 'center',
            fontSize: '18px',
            height: '100%',
            borderRadius: '8px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 0 14px 0 rgba(81,115,255,0.09)',
            width: '100%',
            position: 'relative',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            padding: '0 15px',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
    }),
);
const BarWrapper = glamorous.div<{ theme?: object }>((props) => ({
    position: 'relative',
    [`& > ${`span`}`]: {
        // marginBottom: '15px',
        display: 'block',
        textAlign: 'center',
        with: '100%',
    },
}));

const BarTitleContainer = glamorous.div<{ theme?: object }>((props) => ({
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
    padding: '15px 15px 0px 15px',
    fontSize: '16px',
    fontWeight: 600,
    [`& .${`h1`}`]: {
        display: 'flex',
        alignItems: 'center',
    },
    [`& .${`h2`}`]: {
        fontSize: '2.5rem',
        padding: '10px 0',
    },
    [`& .${`h3`}`]: {
        paddingTop: '10px',
    },
})).withProps({ className: 'rv-chart-title-area' });

const BarContainer = glamorous.div<{ theme?: object }>((props) => ({
    padding: '5px 15px 15px 15px',
    height: '100vh',
    maxHeight: '300px',
    '@media (max-width: 533px)': {
        maxWidth: '350px',
        margin: '0 auto',
    },
    [`& .${`titleWrapper`}`]: {
        textAlign: 'center',
        display: 'inline-block',
        width: '100%',
        padding: '15px',
        [`& .${`title`}`]: {
            fontSize: '18px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`value`}`]: {
            fontSize: '24px',
            fontWeight: 600,
            display: 'block',
            marginBottom: '15px',
        },
        [`& .${`secondary-title`}`]: {
            fontSize: '16px',
            fontWeight: 400,
            display: 'block',
            marginBottom: '15px',
        },
    },
}));

const LoadWrapper = glamorous.div<{ theme?: object }>((props) => ({
    padding: '20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
}));

const TotalDiv = glamorous.div<{ theme?: object }>((props) => ({
    cursor: 'pointer',
    paddingRight: 5,
}));

export enum LastCountEnum {
    LAST_30_Days,
    LAST_90_Days,
    LAST_12_Months,
    ALL_TIME,
}

interface ContactActivityBySubjectChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    selectedOption?: number;
}

interface ChartState {
    last: LastCountEnum;
    grouping: string[];
    counts: number[];
    currentTotal: number;
    periodString: string;
}

export const ContactActivityBySubjectChart: React.FC<ContactActivityBySubjectChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<ContactActivityBySubjectData>(ChartData, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: props.selectedOption === undefined ? LastCountEnum.LAST_12_Months : props.selectedOption,
        grouping: [],
        counts: [],
        currentTotal: 0,
        periodString: '14 Days',
    });

    const getPeriodString = (option: LastCountEnum) => {
        switch (option) {
            case LastCountEnum.LAST_30_Days:
                return 'Last 30 Days';
            case LastCountEnum.LAST_90_Days:
                return 'Last 90 Days';
            case LastCountEnum.LAST_12_Months:
                return 'Last 12 Months';
            default:
                return 'All Time';
        }
    };

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let grouping: string[] = [];
                let counts: number[] = [];
                let currentTotal: number = 0;
                let periodString: string = '';
                if (!error && data && data.statistics && data.statistics.matter 
                    && data.statistics.matter.contactActivitiesBySubject) {
                    var last30Days = data.statistics.matter.contactActivitiesBySubject.last30Days;
                    var last30DaysTotal = data.statistics.matter.contactActivitiesBySubject.summary;
                    var last90Days = data.statistics.matter.contactActivitiesBySubject.last90Days;
                    var last90DaysTotal = data.statistics.matter.contactActivitiesBySubject.summary;
                    var last12Months = data.statistics.matter.contactActivitiesBySubject.last12Months;
                    var last12MonthsTotal = data.statistics.matter.contactActivitiesBySubject.summary;
                    var allTime = data.statistics.matter.contactActivitiesBySubject.allTime;
                    var allTimeTotal = data.statistics.matter.contactActivitiesBySubject.summary;

                    if (state.last === LastCountEnum.LAST_30_Days) {
                        grouping = last30Days.map((s) => s.grouping);
                        counts = last30Days.map((s) => s.count);
                        currentTotal = last30DaysTotal.last30Days;
                        periodString = getPeriodString(state.last);
                    } else if (state.last === LastCountEnum.LAST_90_Days) {
                        grouping = last90Days.map((s) => s.grouping);
                        counts = last90Days.map((s) => s.count);
                        currentTotal = last90DaysTotal.last90Days;
                        periodString = getPeriodString(state.last);
                    } else if (state.last === LastCountEnum.LAST_12_Months) {
                        grouping = last12Months.map((s) => s.grouping);
                        counts = last12Months.map((s) => s.count);
                        currentTotal = last12MonthsTotal.last12Months;
                        periodString = getPeriodString(state.last);
                    } else if (state.last === LastCountEnum.ALL_TIME) {
                        grouping = allTime.map((s) => s.grouping);
                        counts = allTime.map((s) => s.count);
                        currentTotal = allTimeTotal.allTime;
                        periodString = getPeriodString(state.last);
                    }
                }

                return {
                    ...prevState,
                    grouping,
                    counts,
                    currentTotal,
                    periodString,
                };
            });
        }
    },        [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('IsContactActivityBySubjectChartOpen');
        }
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const getLastPeriodComparison = (option: LastCountEnum) => {
        switch (option) {
            case LastCountEnum.LAST_30_Days:
                return 30 + '';
            case LastCountEnum.LAST_90_Days:
                return 90 + '';
            case LastCountEnum.LAST_12_Months:
                return 12 + '';
            default:
                return Number.MAX_SAFE_INTEGER + '';
        }
    };

    const handleTotalClick = (isCurrent: Boolean) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();
            
            searchCriterias.push({
                property: 'IncludeIndividual',
                operator: 'eq',
                comparison: 'true'
            });

            searchCriterias.push({
                property: 'IncludeOrganisation',
                operator: 'eq',
                comparison: 'true'
            });

            searchCriterias.push({
                property: 'LastPeriod',
                operator: 'subject',
                comparison: getLastPeriodComparison(state.last)
            });

            if (props.chartFilter && props.chartFilter.entityType === 8) {
                searchCriterias.push({
                    property: 'NameId',
                    operator: 'eq',
                    comparison: props.chartFilter.entityGuid ?? ''
                });
            }

            const title = `Drilldown from Contact Activities by Subject - ${getPeriodString(state.last)}`;

            props.getSelectedItem(
                searchCriterias, 
                true, 
                title, 
                DrillDownType.notesSummary, 
                'Phone Call',
                {
                    includeEventsAndCampaign: true,
                    isMyActivityOnly: false,
                    entityType: props.chartFilter?.entityType,
                    entityGuid: props.chartFilter?.entityGuid,
                    responsibility: props.chartFilter?.responsibility,
                }
            );
        }
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    if (loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }
    return (
        <React.Fragment>
            {/* tslint:disable-next-line: no-any */}
            <Query<any> query={CurrencySymbol}>
                {(result) => {
                    if (result.loading) {
                        return (
                            <LoadWrapper className="load-wrapper">
                                <RvLoader size="small" />
                            </LoadWrapper>
                        );
                    }

                    return (
                        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.counts) ? ' rv-chart--empty' : ''}`}>
                            <BarWrapper>
                                <IconButtonWrapper aria-label="close">
                                    <HelpIconPopover iconClassName={`${classes.helpIcon} rv-chart-help`}>
                                        {props.description && props.description.indexOf('blocks":') !== -1 ? (
                                            <MUIRichTextEditor value={props.description} readOnly={true} toolbar={false} />
                                        ) : (
                                            <Typography>{props.description}</Typography>
                                        )}
                                    </HelpIconPopover>
                                    <IconButton aria-label="close" onClick={onCloseChart} size="small">
                                        <CloseIcon className={classes.closeIcon} />
                                    </IconButton>
                                </IconButtonWrapper>
                                <BarTitleContainer>
                                    <ChartHeading icon={<ContactMailOutlinedIcon />} title="Contact Activities by Subject" />
                                    <TotalDiv className="h2" onClick={() => handleTotalClick(true)}>
                                        <AnimatedNumber value={state.currentTotal} format={(amount) => `${formatNumber(amount)}`} />
                                    </TotalDiv>
                                    <div className="h3">{state.periodString}</div>
                                </BarTitleContainer>
                                <BarContainer className="rv-chart-bars">
                                    <Bar
                                        ref={chartRef}
                                        // tslint:disable-next-line: no-any
                                        onClick={(event: React.MouseEvent<any>) => {
                                            const _data = getElementAtEvent(chartRef.current, event);
                                            if (_data.length > 0 && props.getSelectedItem) {
                                                const _index = getElementAtEvent(chartRef.current, event)[0].index;
                                                const grouping = state.grouping[_index];

                                                const searchCriterias = new Array<SearchCriteria>();

                                                searchCriterias.push({
                                                    property: 'IncludeIndividual',
                                                    operator: 'eq',
                                                    comparison: 'true'
                                                });
                                    
                                                searchCriterias.push({
                                                    property: 'IncludeOrganisation',
                                                    operator: 'eq',
                                                    comparison: 'true'
                                                });
                                    
                                                searchCriterias.push({
                                                    property: 'LastPeriod',
                                                    operator: 'subject',
                                                    comparison: getLastPeriodComparison(state.last)
                                                });

                                                searchCriterias.push({
                                                    property: 'SubjectList',
                                                    operator: 'eq',
                                                    comparison: grouping
                                                });

                                                if (props.chartFilter && props.chartFilter.entityType === 8) {
                                                    searchCriterias.push({
                                                        property: 'NameGuid',
                                                        operator: 'eq',
                                                        comparison: props.chartFilter.entityGuid ?? ''
                                                    });
                                                }
                                    
                                                const title = `Drilldown from Contact Activities by Subject - ${getPeriodString(state.last)} - ${grouping}`;
                                    
                                                props.getSelectedItem(
                                                    searchCriterias, 
                                                    true, 
                                                    title, 
                                                    DrillDownType.notesSummary, 
                                                    'Phone Call',
                                                    {
                                                        includeEventsAndCampaign: true,
                                                        isMyActivityOnly: false,
                                                        entityType: props.chartFilter?.entityType,
                                                        entityGuid: props.chartFilter?.entityGuid,
                                                        responsibility: props.chartFilter?.responsibility,
                                                    }
                                                );
                                            }
                                        }}
                                        data={{
                                            labels: getLabels(state.grouping),
                                            datasets: [
                                                {
                                                    label: 'Contact Activities',
                                                    ...rvBarDatasetStyle(RV_CHART_COLORS.primary, true),
                                                    data: state.counts,
                                                },
                                            // tslint:disable-next-line: no-any
                                            ] as any,
                                        }}
                                        options={{
                                            indexAxis: 'y' as const,
                                            animation: rvChartAnimation(),
                                            elements: { bar: { borderWidth: 0 } },
                                            responsive: true,
                                            maintainAspectRatio: false,
                                            scales: {
                                                x: {
                                                    grid: rvValueAxisStyle.grid,
                                                    stacked: true,
                                                    suggestedMax: getSuggestedMaxTick(state.counts),
                                                    beginAtZero: true,
                                                    ticks: {
                                                        ...rvValueAxisStyle.ticks,
                                                        maxTicksLimit: getMaxTickLimit(state.counts),
                                                        autoSkip: false,
                                                        callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                                            return `${tickValue}`;
                                                        },
                                                    },
                                                },
                                                y: {
                                                    grid: rvCategoryAxisStyle.grid,
                                                    stacked: true,
                                                    max: 9,
                                                    ticks: {
                                                        ...rvCategoryAxisStyle.ticks,
                                                        autoSkip: false,
                                                    }
                                                }
                                            },
                                            plugins: {
                                                legend: {
                                                    position: 'bottom' as const,
                                                    display: false,
                                                },
                                                title: {
                                                    display: false,
                                                    text: ``,
                                                },
                                                zoom: {
                                                    pan: {
                                                        enabled: true,
                                                        onPan: onPan,
                                                    },
                                                    zoom: {
                                                        wheel: {
                                                            enabled: false,
                                                        },
                                                    },
                                                    // limits: {
                                                    //   y: { min: 0, max: 9 },
                                                    // },
                                                },
                                                tooltip: {
                                                    ...rvTooltipStyle,
                                                    callbacks: {
                                                        // tslint:disable-next-line: no-any
                                                        label: function (context: any) {
                                                            const {
                                                                //   dataIndex,
                                                                //   label,
                                                                formattedValue,
                                                                dataset,
                                                            } = context;

                                                            var currentValue = convertStringNumberToNumber(formattedValue);

                                                            return `${dataset.label}: ${formatNumber(currentValue)}`;
                                                        },
                                                    },
                                                },
                                            },
                                        }}
                                    />
                                    {isChartDataEmpty(state.counts) && (
                                        <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                                    )}
                                </BarContainer>
                                <FormControl component="div">
                                    <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                                        <FormControlLabel value={LastCountEnum.LAST_30_Days} control={<Radio color="primary" />} label="Last 30 Days" />
                                        <FormControlLabel value={LastCountEnum.LAST_90_Days} control={<Radio color="primary" />} label="Last 90 Days" />
                                        <FormControlLabel value={LastCountEnum.LAST_12_Months} control={<Radio color="primary" />} label="Last 12 Months" />
                                        <FormControlLabel value={LastCountEnum.ALL_TIME} control={<Radio color="primary" />} label="All time" />
                                    </RadioGroup>
                                </FormControl>
                            </BarWrapper>
                        </div>
                    );
                }}
            </Query>
        </React.Fragment>
    );
};

const CurrencySymbol = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

export interface ContactActivityBySubjectData {
    statistics: Statistics;
}

export interface Statistics {
  matter: Matter;
}

export interface Matter {
  contactActivitiesBySubject: ContactActivitiesBySubject;
}

export interface ContactActivitiesBySubject {
  allTime:    AllTime[];
  last30Days: AllTime[];
  last90Days: AllTime[];
  last12Months: AllTime[];
  summary:    Summary;
}

export interface AllTime {
  count:    number;
  grouping: string;
}

export interface Summary {
  last30Days:   number;
  allTime:      number;
  last90Days:   number;
  last12Months: number;
}

const ChartData = gql`
query contactActivityBySubjectData(
  $entityType: Int
  $entityGuid: String
  $responsibility: Int
) {
  statistics {
    matter {
      contactActivitiesBySubject(
        entityGuid: $entityGuid
        entityType: $entityType
        responsibility: $responsibility
      ) {
        allTime {
          count
          grouping
        }
        last30Days {
          count
          grouping
        }

        last90Days {
          count
          grouping
        }

        last12Months {
          count
          grouping
        }
        summary {
          last30Days
          allTime
          last90Days
          last12Months
        }
      }
    }
  }
}
`;
